import type { ReactNode } from "react";

// Small stroked line icons (~15px) matching the reference sidebar glyphs. Kept in one
// place so the shell stays focused on layout, not SVG paths.
const icon = (path: ReactNode, size = 15): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    {path}
  </svg>
);

export const HomeIcon = icon(<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />);

export const FriendsIcon = icon(
  <>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </>,
);

// Envelope — the invite inbox (T25).
export const InvitesIcon = icon(
  <>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </>,
);

export const MyTripsIcon = icon(
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </>,
);

export const TemplatesIcon = icon(
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14,2 14,8 20,8" />
  </>,
);

export const ArchiveIcon = icon(
  <>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
  </>,
);

export const SettingsIcon = icon(
  <>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

// Hamburger — opens the sidebar drawer on narrow viewports.
export const MenuIcon = icon(<path d="M3 6h18M3 12h18M3 18h18" />, 20);

// Close (×) — dismisses the drawer.
export const CloseIcon = icon(<path d="M18 6 6 18M6 6l12 12" />, 18);

export const PersonIcon = icon(
  <>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </>,
);

export const LockIcon = icon(
  <>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </>,
);

export const GlobeIcon = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </>,
);

export const SunIcon = icon(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </>,
);

export const PaletteIcon = icon(
  <>
    <path d="M12 2a10 10 0 1 0 0 20c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1.1.9-2 2-2h2.3c1.5 0 2.7-1.2 2.7-2.7C20 6.8 16.4 2 12 2Z" />
    <circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="9.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
  </>,
);

export const CalendarIcon = icon(
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </>,
  11,
);

export const ChevronRightIcon = icon(<polyline points="9 18 15 12 9 6" />, 16);

export const SignOutArrowIcon = icon(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </>,
  14,
);

export const PlusIcon = (size = 18): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const LinkIcon = icon(
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>,
  13,
);

export const ShareIcon = icon(
  <>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </>,
  13,
);

export const EyeIcon = icon(
  <>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
  14,
);

export const TrashIcon = icon(
  <>
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </>,
  14,
);

export const SearchIcon = (size = 13): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// Kit meta-chip icons (line 559/561): 11px, matching the kit's exact stroke widths per
// icon (clock 2.5, document 2) — the shared `icon()` helper is fixed at stroke 2, so
// these are standalone.
export const ClockChipIcon: ReactNode = (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// T28 — "sync trip changes back to the template": two arrows chasing each other round a
// circle. Used on the sync action in the templates-for-this-trip modal.
export const SyncIcon = icon(
  <>
    <polyline points="21 3 21 9 15 9" />
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L3 8" />
    <polyline points="3 21 3 15 9 15" />
    <path d="M3.51 15a9 9 0 0 0 14.85 3.36L21 16" />
  </>,
  13,
);

// Doc 14 — "refresh" pulls a template's later additions into a trip. Deliberately not
// SyncIcon's two-way loop: this one only comes downward, into the list.
export const RefreshDownIcon = icon(
  <>
    <path d="M21 12a9 9 0 1 1-3.5-7.1" />
    <polyline points="21 3 21 9 15 9" />
    <polyline points="9 12 12 15 15 12" />
    <path d="M12 8v7" />
  </>,
  13,
);

export const TemplateChipIcon: ReactNode = (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
  </svg>
);

// Kit `.edit-affordance` icon buttons (lines 57–62): 22px check (save, green) and
// cancel/× (red-hover) shown next to a field while it's being edited.
export const CheckIcon = icon(<polyline points="20 6 9 17 4 12" />, 14);

// Tiny filled checkmark for the assignment-chip "packed/done by" badge — overlaid at an
// avatar's bottom-right corner, so it stays legible at ~8px.
export const CheckBadgeIcon = (size = 8): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const CancelIcon = icon(<path d="M18 6 6 18M6 6l12 12" />, 14);

// Trip-detail topbar clock (13px, stroke 2) — distinct from `ClockChipIcon` (11px,
// stroke 2.5) used in the meta-chip pill; same glyph, different weight/size per kit.
export const ClockIcon13 = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>,
  13,
);

// Trip-detail tab glyphs (13px stroke icons): packing (suitcase), to-do (check square),
// notes (doc). Moved from `TripDetailPage.tsx`'s `TAB_ICONS` (CLAUDE.md rule 7).
export const PackingTabIcon = icon(
  <>
    <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </>,
  13,
);

export const TodoTabIcon = icon(
  <>
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </>,
  13,
);

export const NotesTabIcon = icon(<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />, 13);

// Stacked rows (settings: "stage items by default" sibling) — represents issue #203's
// expanded per-participant display mode vs. a single condensed line.
export const RowsIcon = icon(
  <>
    <rect x="3" y="4" width="18" height="5" rx="1.5" />
    <rect x="3" y="15" width="18" height="5" rx="1.5" />
  </>,
  16,
);

// Drag-handle affordance (2×3 dot grid, 12px) — visual only, no DnD wiring yet. Moved
// from `DragHandle.tsx` (CLAUDE.md rule 7); fill-based dots don't fit the stroke-based
// `icon()` helper above, so this is a standalone export like `PlusIcon`.
export const DragHandleIcon: ReactNode = (
  <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="5" r="1.5" fill="currentColor" />
    <circle cx="9" cy="12" r="1.5" fill="currentColor" />
    <circle cx="9" cy="19" r="1.5" fill="currentColor" />
    <circle cx="15" cy="5" r="1.5" fill="currentColor" />
    <circle cx="15" cy="12" r="1.5" fill="currentColor" />
    <circle cx="15" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

// Brand glyphs for federated sign-in (T22). Filled single-color marks (currentColor)
// rather than the stroked `icon()` helper — brand logos are fill-based.
export const GoogleIcon: ReactNode = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"
    />
  </svg>
);

export const AppleIcon: ReactNode = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"
    />
  </svg>
);

// Packed-suitcase glyph — the sidebar/auth brand mark and (recolored per the chosen
// accent) the favicon built at runtime in favicon.ts. Kept as a raw path string so both
// consumers share one source instead of duplicating the coordinates.
export const LOGO_MARK_PATH =
  "M320.33 127.88C341.91 130.09 367.24 122.22 386.33 135.27C407.37 149.66 405 171.26 405 193.81C405 236.99 405 280.16 405 323.33C405 342.35 405 361.36 405 380.38C405 390.27 406.11 400.92 404.76 410.73C402.76 425.25 392.85 439.12 379.09 444.81C373.91 446.96 368.75 447.25 363.31 448.18C360.86 455.18 361.61 459.65 354.54 464.65C343.41 472.51 326.45 468.98 321.29 455.58C320.37 453.19 320.23 450.49 319.77 448C277.26 448 234.74 448 192.23 448C191.76 450.49 191.63 453.19 190.71 455.58C185.55 468.97 168.58 472.52 157.46 464.65C150.39 459.64 151.13 455.2 148.69 448.18C143.25 447.25 138.1 446.96 132.91 444.81C119.19 439.14 109.19 425.26 107.24 410.74C105.92 400.91 107 390.28 107 380.38C107 361.36 107 342.35 107 323.33C107 280.16 107 236.99 107 193.81C107 171.28 104.62 149.64 125.67 135.27C144.77 122.22 170.08 130.09 191.67 127.88C193.25 123.18 192 116.07 192 111.05C192 98.74 192 86.42 192 74.11C192 66.78 191.21 59.28 195.21 52.8C201.48 42.65 212.79 43 223.27 43C244.78 43 266.29 43 287.8 43C299.91 43 311.26 42.32 317.82 54.59C320.94 60.43 320 67.72 320 74.11C320 86.42 320 98.74 320 111.05C320 116.07 318.75 123.18 320.33 127.88ZM287.99 75C266.66 75 245.34 75 224.01 75C224.01 92.67 224.01 110.33 224.01 127.99C245.34 127.99 266.66 127.99 287.99 127.99C287.99 110.33 287.99 92.67 287.99 75ZM203 192.02C192.33 192.02 181.67 192.02 171 192.02C171 256.01 171 319.99 171 383.98C181.67 383.98 192.33 383.98 203 383.98C203 319.99 203 256.01 203 192.02ZM240 192.01C240 256 240 320 240 383.99C250.67 383.99 261.33 383.99 272 383.99C272 320 272 256 272 192.01C261.33 192.01 250.67 192.01 240 192.01ZM341 192.02C330.33 192.02 319.67 192.02 309 192.02C309 256.01 309 319.99 309 383.98C319.67 383.98 330.33 383.98 341 383.98C341 319.99 341 256.01 341 192.02Z";

// Brand mark for the sidebar/auth "P" square. Only the inner glyph subpath — the
// accent-colored square background comes from the surrounding span, so this renders as
// currentColor to sit on top of it.
export const LogoMarkIcon = ({ size = 20 }: { size?: number }): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
    <path fill="currentColor" fillRule="evenodd" d={LOGO_MARK_PATH} />
  </svg>
);
